import React, { useMemo, useState } from "react";
import AnalysisPanel, { PanelEmpty } from "../AnalysisPanel";
import Pagination from "../../shared/Pagination";
import useClientPagination from "../../../hooks/useClientPagination";
import { NeedsMultipleSyncs } from "./AbsorptionVelocityPanel";
import { getCiReportDemandIndex } from "../../../api/competitiveIntelligence";
import { InferredOccupancy } from "../../../utils/ciFormat";
import { PanelTakeaway, fmtDate, fmtMoneyCompact } from "../shared";
import ThinScroll from "../../shared/ThinScroll";
import SelectMenu from "../../shared/SelectMenu";

const PAGE_SIZE = 15;

// Backend `action_priority` is a free-form string. Map to a stable bucket so the
// badge/row tint logic doesn't depend on exact wording.
const priorityBucket = (action) => {
  if (!action) return "NORMAL";
  const normalized = String(action).toUpperCase();
  if (normalized.startsWith("CRITICAL")) return "CRITICAL";
  if (normalized.startsWith("HIGH")) return "HIGH";
  if (normalized.startsWith("ELEVATED")) return "ELEVATED";
  return "NORMAL";
};

const PRIORITY_BADGE = {
  CRITICAL: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
  HIGH:     "bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300",
  ELEVATED: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
  NORMAL:   "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300",
};

const ROW_TINT = {
  CRITICAL: "bg-red-50/70 dark:bg-red-900/15 hover:bg-red-50 dark:hover:bg-red-900/25",
  HIGH:     "bg-orange-50/60 dark:bg-orange-900/10 hover:bg-orange-50 dark:hover:bg-orange-900/20",
  ELEVATED: "hover:bg-gray-50 dark:hover:bg-gray-800/40",
  NORMAL:   "hover:bg-gray-50 dark:hover:bg-gray-800/40",
};

const WINDOW_BADGE = {
  holiday:         "bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300",
  weekend:         "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
  weekday_sample:  "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300",
};

const PriorityBadge = ({ action }) => {
  const bucket = priorityBucket(action);
  const cls = PRIORITY_BADGE[bucket];
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] uppercase tracking-wide font-semibold whitespace-nowrap ${cls}`}>
      {action || "—"}
    </span>
  );
};

const WindowBadge = ({ type, name }) => {
  const cls = WINDOW_BADGE[type] || WINDOW_BADGE.weekday_sample;
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${cls}`} title={name || ""}>
      {name || (type ? type.replace("_", " ") : "—")}
    </span>
  );
};

// 0–100 score bar: emerald (low) → amber (mid) → red (high).
const scoreColor = (value) => {
  if (value == null) return "bg-gray-300 dark:bg-gray-700";
  if (value >= 70) return "bg-red-500";
  if (value >= 40) return "bg-amber-500";
  return "bg-emerald-500";
};

const IndexBar = ({ value }) => {
  const pct = value == null ? 0 : Math.max(0, Math.min(100, Number(value)));
  return (
    <div className="flex items-center gap-2 min-w-[120px]">
      <span className="text-base font-bold text-gray-900 dark:text-gray-100 tabular-nums w-8 text-right">{value ?? "—"}</span>
      <div className="flex-1 h-1.5 rounded-full bg-gray-200 dark:bg-gray-800 overflow-hidden">
        <div className={`h-full ${scoreColor(value)}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

const PRIORITY_ORDER = { CRITICAL: 0, HIGH: 1, ELEVATED: 2, NORMAL: 3 };

// Sort the full dataset before pagination — the RPC returns all rows in a
// fixed order, so we sort here to keep the visible slice consistent with the
// active sort choice across pages.
const sortRows = (rows, sortBy) => {
  const out = [...rows];
  if (sortBy === "score_desc") return out.sort((rowA, rowB) => (rowB.demand_index ?? 0) - (rowA.demand_index ?? 0));
  if (sortBy === "priority") {
    return out.sort((rowA, rowB) => {
      const priorityDiff = (PRIORITY_ORDER[priorityBucket(rowA.action_priority)] ?? 99) -
                (PRIORITY_ORDER[priorityBucket(rowB.action_priority)] ?? 99);
      if (priorityDiff !== 0) return priorityDiff;
      return (rowB.demand_index ?? 0) - (rowA.demand_index ?? 0);
    });
  }
  if (sortBy === "days_out") return out.sort((rowA, rowB) => (rowA.days_out ?? 9999) - (rowB.days_out ?? 9999));
  return out.sort((rowA, rowB) => (rowA.check_in || "").localeCompare(rowB.check_in || ""));
};

const DemandIndexBody = ({ rows, sortBy, onSortChange, totalItems }) => {
  if (!rows.length) {
    return <PanelEmpty message="No demand index data for the selected category." />;
  }
  const sorted = sortRows(rows, sortBy);

  return (
    <div>
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800 flex items-center gap-3 flex-wrap">
        <span className="inline-flex items-center gap-1.5">
          <span className="text-sm text-gray-600 dark:text-gray-300">Sort this page:</span>
          <SelectMenu
            ariaLabel="Sort dates"
            value={sortBy}
            onChange={onSortChange}
            options={[
              { value: "score_desc", label: "Demand index (high → low)" },
              { value: "priority", label: "Action priority (urgent first)" },
              { value: "days_out", label: "Days out (soonest first)" },
              { value: "date", label: "Check-in date" },
            ]}
          />
        </span>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          Showing {rows.length} of {totalItems ?? rows.length} {(totalItems ?? rows.length) === 1 ? "date" : "dates"}
        </span>
      </div>
      <ThinScroll className="overflow-x-auto responsive-card-table-wrapper">
        <table className="w-full min-w-[1280px] text-sm table-fixed responsive-card-table">
          <colgroup>
            <col style={{ width: "10%" }} />
            <col style={{ width: "12%" }} />
            <col style={{ width: "12%" }} />
            <col style={{ width: "14%" }} />
            <col style={{ width: "8%" }} />
            <col style={{ width: "8%" }} />
            <col style={{ width: "8%" }} />
            <col style={{ width: "8%" }} />
            <col style={{ width: "10%" }} />
            <col style={{ width: "10%" }} />
          </colgroup>
          <thead className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
            <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
              <th className="px-3 py-2.5 text-left font-semibold">Check-in</th>
              <th className="px-3 py-2.5 text-left font-semibold">Window</th>
              <th className="px-3 py-2.5 text-left font-semibold">Index</th>
              <th className="px-3 py-2.5 text-left font-semibold">Action</th>
              <th className="px-3 py-2.5 text-right font-semibold">Your Rate</th>
              <th className="px-3 py-2.5 text-right font-semibold">Market Rate</th>
              <th className="px-3 py-2.5 text-right font-semibold">Your Occupancy</th>
              <th className="px-3 py-2.5 text-right font-semibold">Comp Occupancy</th>
              <th className="px-3 py-2.5 text-right font-semibold whitespace-nowrap" title="Competitors with Limited Availability">Limited Avail.</th>
              <th className="px-3 py-2.5 text-right font-semibold">Min Stay</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((row, index) => {
              const bucket = priorityBucket(row.action_priority);
              const tint = ROW_TINT[bucket] || "hover:bg-gray-50 dark:hover:bg-gray-800/50";
              const totalComps = row.total_competitors ?? 0;
              const ltdAvail = row.competitors_sold_out ?? 0;
              const minStay = row.competitors_min_stay ?? 0;
              return (
                <tr key={`${row.check_in}-${index}`} className={`border-b dark:border-gray-800 transition ${tint}`}>
                  <td data-label="Check-in" className="px-3 py-2.5 align-top whitespace-nowrap">
                    <div className="font-medium text-gray-900 dark:text-gray-100">{fmtDate(row.check_in)}</div>
                    {row.days_out != null && (
                      <div className="text-[11px] text-gray-500 dark:text-gray-400">{row.days_out}d out</div>
                    )}
                  </td>
                  <td data-label="Window" className="px-3 py-2.5 align-top whitespace-nowrap">
                    <WindowBadge type={row.date_window_type} name={row.date_window_name} />
                  </td>
                  <td data-label="Index" className="px-3 py-2.5 align-top">
                    <IndexBar value={row.demand_index} />
                  </td>
                  <td data-label="Action" className="px-3 py-2.5 align-top">
                    <PriorityBadge action={row.action_priority} />
                  </td>
                  <td data-label="Your Rate" className="px-3 py-2.5 align-top text-right tabular-nums text-gray-900 dark:text-gray-100">{fmtMoneyCompact(row.your_rate)}</td>
                  <td data-label="Market Rate" className="px-3 py-2.5 align-top text-right tabular-nums text-gray-700 dark:text-gray-300">{fmtMoneyCompact(row.market_rate)}</td>
                  <td data-label="Your Occupancy" className="px-3 py-2.5 align-top text-right tabular-nums">
                    <InferredOccupancy value={row.your_occupancy} className="text-gray-900 dark:text-gray-100" />
                  </td>
                  <td data-label="Comp Occupancy" className="px-3 py-2.5 align-top text-right tabular-nums">
                    <InferredOccupancy value={row.comp_occupancy} className="text-gray-700 dark:text-gray-300" />
                  </td>
                  <td data-label="Limited Avail." className="px-3 py-2.5 align-top text-right tabular-nums text-gray-700 dark:text-gray-300">
                    {totalComps ? `${ltdAvail} / ${totalComps}` : "—"}
                  </td>
                  <td data-label="Min Stay" className="px-3 py-2.5 align-top text-right tabular-nums text-gray-700 dark:text-gray-300">
                    {totalComps ? `${minStay} / ${totalComps}` : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </ThinScroll>
    </div>
  );
};

const DemandIndexBodyPaginated = ({ rows, sortBy, onSortChange }) => {
  const sorted = useMemo(() => sortRows(rows, sortBy), [rows, sortBy]);
  const { page, setPage, pageItems, totalPages, totalItems } = useClientPagination(sorted, { pageSize: PAGE_SIZE });
  return (
    <>
      <DemandIndexBody rows={pageItems} sortBy={sortBy} onSortChange={onSortChange} totalItems={totalItems} />
      {totalPages > 1 && (
        <div className="px-5 pt-1 pb-3 border-t border-gray-200 dark:border-gray-800">
          <Pagination
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
            totalItems={totalItems}
            perPage={PAGE_SIZE}
            label="dates"
          />
        </div>
      )}
    </>
  );
};

const DemandIndexPanel = ({ clientId, categoryCode, say }) => {
  const [sortBy, setSortBy] = useState("score_desc");

  return (
    <AnalysisPanel
      id={`demand-index-${clientId}-${categoryCode || "all"}`}
      title="Demand Index"
      tip="A 0–100 composite score per date combining occupancy, rate gap, competitor scarcity, and inventory pressure. Sort by score to see which dates deserve a rate move first."
      description="0–100 composite demand score per date, blending occupancy, rate gap, competitor scarcity, and inventory. Higher action priorities flag where to raise rates first."
      badge={categoryCode ? categoryCode.toUpperCase() : null}
      fetcher={() => getCiReportDemandIndex(clientId, { categoryCode: categoryCode || "rv" })}
      deps={[clientId, categoryCode]}
    >
      {({ data }) => {
        // Backend returns the placeholder envelope when fewer than 2 sync cycles
        // exist for this client+category. Show the shared waiting state.
        if (data && !Array.isArray(data) && data.status === "needs_multiple_syncs") {
          return <NeedsMultipleSyncs status={data.status} message={data.message} currentSync={data.current_sync} latestSyncDate={data.latest_sync_date} />;
        }
        const items = Array.isArray(data)
          ? data
          : (Array.isArray(data?.items) ? data.items : []);
        return (
          <>
            <PanelTakeaway panelKey="demand_index" say={say} />
            <DemandIndexBodyPaginated rows={items} sortBy={sortBy} onSortChange={setSortBy} />
          </>
        );
      }}
    </AnalysisPanel>
  );
};

export default DemandIndexPanel;
